import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrandAndContact } from "@/hooks/useBrandAndContact";
import { SERVICES } from "@/data/services";
import ShareBar from "@/components/ShareBar";

const Footer = () => {
  const { t, lang } = useLanguage();
  const CONTACT = useBrandAndContact();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[#C5A059]/15 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        {/* Top statement */}
        <div className="grid md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-7">
            <p className="overline kw-accent-line mb-6">
              Kiwami · {lang === "fr" ? "Maîtrise" : "Mastery"}
            </p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] text-[#F2EBE5]">
              {lang === "fr" ? (
                <>
                  L&apos;image <em className="text-[#C5A059]">taillée</em>
                  <br />
                  avec précision.
                </>
              ) : (
                <>
                  The image <em className="text-[#C5A059]">sculpted</em>
                  <br />
                  with precision.
                </>
              )}
            </h2>
          </div>

          <div className="md:col-span-5 flex flex-col justify-end gap-6">
            <a
              href={CONTACT.phoneHref}
              data-testid="footer-phone-link"
              className="group flex items-center justify-between border-b border-[#F2EBE5]/10 pb-4 hover:border-[#C5A059] transition-colors"
            >
              <span className="flex items-center gap-4">
                <Phone className="w-4 h-4 text-[#C5A059]" strokeWidth={1.25} />
                <span className="font-serif-display text-xl text-[#F2EBE5]">
                  {CONTACT.phone}
                </span>
              </span>
              <ArrowUpRight
                className="w-4 h-4 text-[#A19E9C] group-hover:text-[#C5A059] transition-colors"
                strokeWidth={1.25}
              />
            </a>

            <a
              href={CONTACT.emailHref}
              data-testid="footer-email-link"
              className="group flex items-center justify-between border-b border-[#F2EBE5]/10 pb-4 hover:border-[#C5A059] transition-colors"
            >
              <span className="flex items-center gap-4">
                <Mail className="w-4 h-4 text-[#C5A059]" strokeWidth={1.25} />
                <span className="font-serif-display text-xl text-[#F2EBE5]">
                  {CONTACT.email}
                </span>
              </span>
              <ArrowUpRight
                className="w-4 h-4 text-[#A19E9C] group-hover:text-[#C5A059] transition-colors"
                strokeWidth={1.25}
              />
            </a>
          </div>
        </div>

        {/* Columns */}
        <div className="grid md:grid-cols-12 gap-12 border-t border-[#F2EBE5]/8 pt-14">
          <div className="md:col-span-4">
            <p className="font-serif-display text-2xl italic text-[#F2EBE5] mb-3">
              Kiwami Studios &amp; Co
            </p>
            <p className="text-sm text-[#A19E9C] leading-relaxed max-w-sm">
              {t("brand.baseline")}
            </p>
            <p className="mt-6 overline kw-accent-line">
              {CONTACT.city}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="kw-label mb-5">{t("footer.nav_title")}</p>
            <ul className="space-y-3">
              {[
                { to: "/", label: t("nav.home") },
                { to: "/services", label: t("nav.services") },
                { to: "/formation", label: t("nav.training") },
                { to: "/a-propos", label: t("nav.about") },
                { to: "/contact", label: t("nav.contact") },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="kw-label mb-5">{t("footer.services_title")}</p>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                  >
                    {t(`services.${s.key}.label`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="kw-label mb-5">{t("footer.contact_title")}</p>
            <ul className="space-y-3">
              <li>
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                >
                  WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.instagramHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5" strokeWidth={1.25} />
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.emailHref}
                  className="text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                >
                  Email
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Share bar — appears on every page via the global footer */}
        <ShareBar />

        {/* Bottom line */}
        <div className="mt-16 pt-8 border-t border-[#F2EBE5]/8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs uppercase tracking-[0.25em] text-[#A19E9C]">
            © {year} Kiwami Studios &amp; Co · {t("footer.rights")}
          </p>
          <p className="text-xs uppercase tracking-[0.25em] text-[#A19E9C]">
            {t("footer.made_in")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
